import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { dataService, plannedTotalDailyDose } from '../../services/dataService';
import {
  InsulinProduct,
  InsulinScheduleView,
  InsulinSlot
} from '../../types/domain';
import InsulinProductSelect, { ProductSelection } from '../components/InsulinProductSelect';

/** The three SoW 3.1 slots, plus the "Tambah Jadwal" extra. */
const SLOTS: { slot: InsulinSlot; label: string }[] = [
  { slot: 'pagi', label: 'Pagi' },
  { slot: 'siang', label: 'Siang' },
  { slot: 'malam', label: 'Malam' }
];

interface DraftEntry {
  id?: string;
  slot: InsulinSlot;
  slotLabel: string;
  timeOfDay: string;
  product: ProductSelection;
  dose: string;
}

const emptyDraft = (slot: InsulinSlot, label: string): DraftEntry => ({
  slot,
  slotLabel: label,
  timeOfDay: '',
  product: { kind: 'none' },
  dose: ''
});

export default function InsulinSchedulePage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState<InsulinProduct[]>([]);
  const [schedule, setSchedule] = useState<InsulinScheduleView[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState<DraftEntry | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      return;
    }
    setLoading(true);
    try {
      const [catalog, entries] = await Promise.all([
        dataService.getInsulinProducts(),
        dataService.getSchedule(user.id)
      ]);
      setProducts(catalog);
      setSchedule(entries);
    } catch (err) {
      console.error(err);
      setError('Gagal memuat jadwal insulin.');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const tdd = plannedTotalDailyDose(schedule);
  const usedSlots = new Set(schedule.map(entry => entry.slot));

  const handleSave = async () => {
    if (!user || !draft) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await dataService.saveScheduleEntry(user.id, {
        id: draft.id,
        slot: draft.slot,
        slotLabel: draft.slotLabel,
        timeOfDay: draft.timeOfDay || null,
        productId: draft.product.kind === 'product' ? draft.product.id : null,
        customProductName: draft.product.kind === 'other' ? draft.product.name : null,
        dose: Number(draft.dose),
        sortOrder: SLOTS.findIndex(s => s.slot === draft.slot)
      });
      setDraft(null);
      setNotice('Jadwal insulin tersimpan.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Gagal menyimpan jadwal insulin.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async (entry: InsulinScheduleView) => {
    if (!user) {
      return;
    }
    if (!window.confirm(`Hapus jadwal ${entry.slotLabel}?`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await dataService.removeScheduleEntry(user.id, entry.id);
      setNotice('Jadwal dihapus.');
      await load();
    } catch (err: any) {
      setError(err?.message || 'Gagal menghapus jadwal.');
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (entry: InsulinScheduleView) => {
    setNotice(null);
    setError(null);
    setDraft({
      id: entry.id,
      slot: entry.slot,
      slotLabel: entry.slotLabel,
      timeOfDay: entry.timeOfDay?.slice(0, 5) ?? '',
      product: entry.productId
        ? { kind: 'product', id: entry.productId }
        : { kind: 'other', name: entry.customProductName ?? '' },
      dose: String(entry.dose)
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[50vh]">
        <div className="text-xl text-gray-600">Menyiapkan jadwal insulin...</div>
      </div>
    );
  }

  return (
    <div className="screen min-h-[100dvh] h-full">
      <div className="screen-card" role="group" aria-labelledby="schedule-title">
        <div id="schedule-title" className="section-title">
          Jadwal insulin
        </div>
        <div className="subtitle">
          Atur insulin dan dosis untuk setiap waktu suntik.
        </div>

        <div className="tdd-card">
          <span className="tdd-label">Total Daily Dose (rencana)</span>
          <span className="tdd-value">
            {tdd} <span className="tdd-unit">unit / hari</span>
          </span>
          <span className="tdd-note">
            Dijumlahkan otomatis dari {schedule.length} jadwal aktif.
          </span>
        </div>

        {schedule.length === 0 ? (
          <div className="subtitle" style={{ marginTop: 12 }}>
            Belum ada jadwal insulin. Tambahkan jadwal pertama di bawah.
          </div>
        ) : (
          <ul className="schedule-list">
            {schedule.map(entry => (
              <li key={entry.id} className="schedule-item">
                <div className="schedule-item-main">
                  <span className="schedule-slot">{entry.slotLabel}</span>
                  <span className="schedule-product">{entry.productLabel}</span>
                  {entry.timeOfDay ? (
                    <span className="schedule-time">{entry.timeOfDay.slice(0, 5)}</span>
                  ) : null}
                </div>
                <div className="schedule-item-side">
                  <span className="schedule-dose">{entry.dose} unit</span>
                  <button
                    type="button"
                    className="schedule-action"
                    disabled={busy}
                    onClick={() => startEdit(entry)}
                  >
                    Ubah
                  </button>
                  <button
                    type="button"
                    className="schedule-action schedule-action--danger"
                    disabled={busy}
                    onClick={() => handleRemove(entry)}
                  >
                    Hapus
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {draft ? (
          <div className="schedule-form">
            <div className="subtitle">
              {draft.id ? `Ubah jadwal ${draft.slotLabel}` : 'Tambah jadwal'}
            </div>

            {draft.slot === 'tambahan' ? (
              <div className="field">
                <label htmlFor="slot-label">Nama jadwal</label>
                <input
                  id="slot-label"
                  type="text"
                  value={draft.slotLabel}
                  placeholder="Misal: Sebelum olahraga"
                  onChange={e => setDraft({ ...draft, slotLabel: e.target.value })}
                />
              </div>
            ) : null}

            <div className="field">
              <label htmlFor="schedule-product">Jenis insulin</label>
              <InsulinProductSelect
                id="schedule-product"
                products={products}
                value={draft.product}
                disabled={busy}
                onChange={product => setDraft({ ...draft, product })}
              />
            </div>

            <div className="field">
              <label htmlFor="schedule-dose">Dosis (unit)</label>
              <input
                id="schedule-dose"
                type="number"
                inputMode="decimal"
                min={0.5}
                max={200}
                step={0.5}
                value={draft.dose}
                onChange={e => setDraft({ ...draft, dose: e.target.value })}
              />
            </div>

            <div className="field">
              <label htmlFor="schedule-time">Jam (opsional)</label>
              <input
                id="schedule-time"
                type="time"
                value={draft.timeOfDay}
                onChange={e => setDraft({ ...draft, timeOfDay: e.target.value })}
              />
            </div>

            {error ? <div className="target-band-error">{error}</div> : null}

            <div className="target-actions">
              <button type="button" className="btn-primary" disabled={busy} onClick={handleSave}>
                {busy ? 'Menyimpan...' : 'Simpan jadwal'}
              </button>
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => setDraft(null)}
              >
                Batal
              </button>
            </div>
          </div>
        ) : (
          <div className="schedule-add">
            {SLOTS.filter(s => !usedSlots.has(s.slot)).map(s => (
              <button
                key={s.slot}
                type="button"
                className="btn-secondary"
                onClick={() => setDraft(emptyDraft(s.slot, s.label))}
              >
                + {s.label}
              </button>
            ))}
            {/* SoW 3.1: for patients on more than three injections a day. */}
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setDraft(emptyDraft('tambahan', ''))}
            >
              + Tambah Jadwal
            </button>
          </div>
        )}

        {notice && !draft ? (
          <div className="subtitle" style={{ marginTop: 10 }}>
            {notice}
          </div>
        ) : null}
        {error && !draft ? <div className="target-band-error">{error}</div> : null}

        <div className="next-actions" style={{ marginTop: 16 }}>
          <button type="button" className="btn-secondary" onClick={() => navigate('/')}>
            Kembali
          </button>
        </div>
      </div>
    </div>
  );
}
